/**
 * /well-testing/tickets — the Well Testing field ticket register
 * (GET /well-testing/tickets, fieldiq-api#1253, WT-4).
 *
 * A ticket is drafted from its job's shift logs (job → Tickets → Create
 * ticket), so this register has no New button; it says where tickets come
 * from. The status and customer filters live in the URL. Totals are the API's: a draft has
 * no snapshot total yet, so it reads `—` rather than a figure nobody approved.
 */
import React from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ClipboardList, FileCheck2, Send } from 'lucide-react';
import { PageHeaderBand } from '../../../components/layout/PageHeaderBand';
import { displayValue } from '../../../features/fieldiq-handoff/contracts';
import { useWtCustomers, useWtMeta } from '../../../hooks/useWellTesting';
import { useWtTickets } from '../../../hooks/useWtTickets';
import { fmtCurrency } from '../../../lib/display-labels';
import { queryLoadFailure } from '../../../lib/load-failure';
import { wtScopeText, wtTicketStatusSkin, WT_TICKET_STATUS_SKIN } from '../../../lib/wt-tickets';
import type { WtTicket, WtTicketCatalog } from '../../../types/wt-tickets';
import { useOpenRecord } from '../../downhole/recordPage';
import { WT_EYEBROW, WT_INPUT, WtBadge, WtChip, WtChipRow, WtEmpty, WtPanel, WtRegister, type WtColumn } from '../WtBits';

/** The figure a register row shows: the office snapshot once signed, the priced-lines sum never (a list row has no lines). */
export const wtTicketTotalText = (t: WtTicket) => fmtCurrency(t.totalAmount, t.currency);

export default function WtTicketsScreen() {
  const navigate = useNavigate();
  const openRecord = useOpenRecord();
  const [searchParams, setSearchParams] = useSearchParams();
  const status = searchParams.get('status') ?? '';
  const customer = searchParams.get('customer') ?? '';
  const setParam = (key: string, next: string) => {
    const p = new URLSearchParams(searchParams);
    if (next) p.set(key, next);
    else p.delete(key);
    setSearchParams(p, { replace: true });
  };
  const setStatus = (next: string) => setParam('status', next);
  const catalog = (useWtMeta().data?.data ?? null) as WtTicketCatalog | null;
  const customers = useWtCustomers().data ?? [];
  const listQuery = useWtTickets({ status: status || undefined, customer_id: customer || undefined });
  const allQuery = useWtTickets();
  const tickets = listQuery.data?.data ?? [];
  const all = allQuery.isLoading || queryLoadFailure(allQuery) ? undefined : allQuery.data?.data;
  const countOf = (pick: (t: WtTicket) => boolean) => displayValue(all ? all.filter(pick).length : undefined);
  const statuses = catalog?.ticketStatuses?.length
    ? catalog.ticketStatuses
    : Object.entries(WT_TICKET_STATUS_SKIN).map(([value, s]) => ({ value, label: s.label }));

  const columns: WtColumn<WtTicket>[] = [
    { key: 'number', header: 'Ticket', render: (t) => <span className="font-mono font-extrabold text-white">{t.ticketNumber || '—'}</span> },
    { key: 'job', header: 'Job', render: (t) => (<><p className="font-mono">{t.jobNumber || '—'}</p><p className="mt-0.5 text-xs text-fieldiq-muted">{t.wellName || '—'}</p></>) },
    { key: 'customer', header: 'Customer', render: (t) => t.customerName || '—' },
    { key: 'scope', header: 'Covers', render: (t) => wtScopeText(t) },
    { key: 'status', header: 'Status', render: (t) => <WtBadge skin={wtTicketStatusSkin(t.status)} value={t.status} /> },
    { key: 'total', header: 'Total', align: 'right', render: (t) => <span className="font-mono">{wtTicketTotalText(t)}</span> },
  ];

  return (
    <div data-testid="wt-tickets-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={WT_EYEBROW}
        title="Field Tickets"
        description="Tickets drafted from the shift logs, approved by the office and signed by the company man."
        onBack={() => navigate(-1)}
        backLabel="Back"
        stats={[
          { icon: <ClipboardList className="size-4" aria-hidden="true" />, label: 'Drafts', tone: 'neutral', loading: allQuery.isLoading, value: countOf((t) => t.status === 'draft') },
          { icon: <Send className="size-4" aria-hidden="true" />, label: 'With the customer', tone: 'accent', loading: allQuery.isLoading, value: countOf((t) => t.status === 'office_approved') },
          { icon: <FileCheck2 className="size-4" aria-hidden="true" />, label: 'Customer approved', tone: 'success', loading: allQuery.isLoading, value: countOf((t) => t.status === 'customer_approved') },
        ]}
      />
      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          <p className="text-sm text-fieldiq-muted">
            A ticket is drafted from its job: open the <Link className="underline" to="/well-testing/jobs">job</Link>, then Tickets → Create ticket.
          </p>
          <WtChipRow label="Filter by status">
            <WtChip label="All" active={!status} onClick={() => setStatus('')} count={allQuery.isLoading ? undefined : countOf(() => true)} />
            {statuses.map((s) => (
              <WtChip key={s.value} label={s.label} active={status === s.value} onClick={() => setStatus(s.value)} count={allQuery.isLoading ? undefined : countOf((t) => t.status === s.value)} />
            ))}
          </WtChipRow>
          <div className="grid gap-3 sm:grid-cols-[16rem]">
            <select className={WT_INPUT} aria-label="Filter by customer" value={customer} onChange={(e) => setParam('customer', e.target.value)}>
              <option value="">Every customer</option>
              {customers.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <WtPanel eyebrow="Ticket register" title="Field tickets" testId="wt-ticket-register" footer={!listQuery.isLoading && tickets.length ? `${tickets.length} ticket${tickets.length === 1 ? '' : 's'}` : undefined}>
            <WtRegister
              query={listQuery}
              rows={tickets}
              columns={columns}
              rail={(t) => wtTicketStatusSkin(t.status).rail}
              onOpen={(t) => openRecord(`/well-testing/tickets/${t.id}`)}
              subject="field tickets"
              testId="wt-ticket"
              minWidth="min-w-[860px]"
              empty={status || customer ? <WtEmpty>No ticket matches these filters.</WtEmpty> : <WtEmpty>No field tickets yet. Draft one from a job.</WtEmpty>}
              card={(t) => (
                <>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate font-mono text-sm font-extrabold text-white">{t.ticketNumber || '—'}</p>
                      <p className="mt-0.5 text-xs text-fieldiq-muted">{t.jobNumber || '—'} · {t.wellName || '—'} · {t.customerName || '—'}</p>
                    </div>
                    <WtBadge skin={wtTicketStatusSkin(t.status)} value={t.status} />
                  </div>
                  <p className="mt-2 text-xs text-fieldiq-text">
                    {wtScopeText(t)} · Total {wtTicketTotalText(t)}
                  </p>
                </>
              )}
            />
          </WtPanel>
        </div>
      </section>
    </div>
  );
}
